import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router";
import {
    Breadcrumbs as FoodsBreadcrumbs,
    FoodsIcon,
    FoodsTabs,
    FoodsTabTemplateOption,
    SectionTab,
    SectionTabs,
} from "@foodpilot/foods";
import { usePackagingContext } from "./PackagingContext";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { ActiveOrganizationContextHeader } from "src/components/ActiveOrganizationContextHeader";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { Breadcrumbs as ResponsiveBreadcrumbs, type BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";

type SectionKeys = "scores" | "questionnaire" | "informations";
type PackagingTab = SectionTab & { title?: string };
type PackagingSections = Partial<Record<SectionKeys, PackagingTab>>;

type PackagingDetailsProps = {
    selectedSection: SectionKeys;
};
export const PackagingPrimaryTabs = (props: PackagingDetailsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isProductUxV2 = useIsProductUxV2();

    const { selectedSection } = props;
    const { selectedCompany } = useFoodpilotContext();
    const { packaging } = usePackagingContext();

    if (packaging === undefined || selectedCompany === null) {
        return;
    }

    const sections: PackagingSections = {};

    sections["scores"] = {
        name: t("scores_indicateurs_cles"),
        icon: <FoodsIcon size={2} icon="scoresIndicators" />,
        onClick: () => {
            const url = URLHandler.packaging.scores(selectedCompany.id, packaging.id);
            navigate(url);
        },
    };

    sections["informations"] = {
        name: t("informations"),
        icon: <FoodsIcon size={2} icon="informations" />,
        onClick: () => {
            const url = URLHandler.packaging.information(selectedCompany.id, packaging.id);
            navigate(url);
        },
    };

    if (isProductUxV2) {
        const scoreUrl = URLHandler.packaging.scores(selectedCompany.id, packaging.id);
        const informationUrl = URLHandler.packaging.information(selectedCompany.id, packaging.id);
        const tabs: FoodsTabTemplateOption<SectionKeys>[] = [
            {
                id: "scores",
                name: (
                    <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                        <Box display="flex" paddingBottom={theme.spacing(0.25)}>
                            <FoodsIcon size={2} icon="scoresIndicators" />
                        </Box>
                        <Typography variant="body-bold" sx={{ textTransform: "capitalize" }}>
                            {t("scores_indicateurs_cles")}
                        </Typography>
                    </Stack>
                ),
                TabProps: { onClick: () => navigate(scoreUrl) },
            },
            {
                id: "informations",
                name: (
                    <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                        <Box display="flex" paddingBottom={theme.spacing(0.25)}>
                            <FoodsIcon size={2} icon="informations" />
                        </Box>
                        <Typography variant="body-bold">{t("informations")}</Typography>
                    </Stack>
                ),
                TabProps: { onClick: () => navigate(informationUrl) },
            },
        ];
        const selectedTabIndex = tabs.findIndex((tab) => tab.id === selectedSection);
        const packagingListUrl = URLHandler.packaging.list(selectedCompany.id);
        const breadcrumbItems: BreadcrumbType[] = [
            {
                label: t("packagings.tabs.all", "All packagings"),
                action: () => navigate(packagingListUrl),
            },
            { label: packaging.name },
        ];

        return (
            <Box
                component="div"
                data-testid="packaging-v2-details-shell"
                sx={{
                    marginInlineEnd: "auto",
                    marginInlineStart:
                        isMobile ? "auto" : `calc(var(--app-navigation-overlap, ${theme.spacing(2.5)}) * -1)`,
                }}
            >
                <Box
                    data-testid="packaging-v2-details-header"
                    sx={{ backgroundColor: theme.custom.grey[100], position: "relative" }}
                >
                    <Box sx={{ paddingInline: theme.spacing(6), paddingTop: theme.spacing(3) }}>
                        {isMobile ?
                            <ResponsiveBreadcrumbs items={breadcrumbItems} />
                        :   <FoodsBreadcrumbs
                                ariaLabel={t("common.navigation.breadcrumb", "Breadcrumb")}
                                ancestors={[
                                    {
                                        id: "packaging-details-list",
                                        label: breadcrumbItems[0].label,
                                        onSelect: breadcrumbItems[0].action!,
                                    },
                                ]}
                                current={{ id: "packaging-details-current", label: packaging.name }}
                            />
                        }
                    </Box>
                    <Box sx={{ marginTop: theme.spacing(1), paddingInline: theme.spacing(6) }}>
                        <FoodsTabs
                            forceScrollable
                            tabs={tabs}
                            selectedTabIndex={selectedTabIndex >= 0 ? selectedTabIndex : 0}
                            tabVariant="primary"
                            TabsProps={{ slotProps: { list: { sx: { gap: theme.spacing(4.5) } } } }}
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            borderBottomColor: theme.custom.strokes.containerDivider.color,
                            borderBottomStyle: theme.custom.strokes.containerDivider.style,
                            borderBottomWidth: theme.custom.strokes.containerDivider.width,
                            bottom: 0,
                            left: 0,
                            pointerEvents: "none",
                            position: "absolute",
                            right: 0,
                        }}
                    />
                </Box>
                <Stack
                    data-testid="packaging-v2-details-content"
                    padding={theme.spacing(3, 6)}
                    sx={{ backgroundColor: theme.custom.grey[300], minHeight: "100%" }}
                >
                    <Outlet />
                </Stack>
            </Box>
        );
    }

    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto" }}>
            <Box
                sx={{
                    width: "auto",
                    background: "#fff",
                    padding: "28px 52px",
                    paddingBottom: 0,
                    position: "relative",
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                }}
            >
                <Stack direction="row" alignItems="center" gap="16px">
                    <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                        <ArrowBackIcon
                            style={{
                                cursor: "pointer",
                                margin: "8px",
                            }}
                            onClick={() => navigate(URLHandler.packaging.list(selectedCompany.id))}
                        />
                        <ActiveOrganizationContextHeader
                            title={<Typography variant="h1">{packaging.name}</Typography>}
                        />
                    </Box>
                </Stack>

                <Divider sx={{ marginTop: "28px" }} />
                <SectionTabs
                    tabs={Object.values(sections)}
                    value={Object.keys(sections).indexOf(selectedSection)}
                    renderComponents={false}
                    sx={{
                        position: "relative",
                        bottom: "-1px",
                    }}
                />
            </Box>

            <Stack
                gap="28px"
                sx={{
                    padding: theme.spacing(3, 6),
                }}
            >
                <Outlet />
            </Stack>
        </Box>
    );
};
